// @djsem/analysis-contract — Platform Playlist Integration (Prompts 04E/04F/04G/04H).
//
// Provider-neutral + Spotify/SoundCloud-specific adapters for playlist import, track
// matching against DJSEMLibraryTrack, DJSEM Set/Playlist creation from confirmed matches,
// platform export dry-run plans, and safe remote write orchestration.
//
// HARD RULES (from all four prompts):
//   • NEVER fetch protected Spotify media files or capture SC stream data.
//   • NEVER store OAuth tokens in browser/frontend — server-side refs only.
//   • NEVER request write scopes for import-only flows.
//   • NEVER mutate remote playlists without explicit user confirmation.
//   • Platform track refs are NOT DJSEM-owned audio files.
//   • Import grants NO rights to analyse, split, transcribe, render, or commercially use.

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "@djsem/shared";
import { SeqCounter } from "@djsem/shared";
import { id, normalize } from "./internal";
import type { DJSEMSet } from "../setBuilder";
import { CreateDJSEMSetFromPlatformInput, DJSEMPlatformPlaylistAuditReport, DJSEMPlatformPlaylistCapabilities, DJSEMPlatformPlaylistExportItem, DJSEMPlatformPlaylistExportPlan, DJSEMPlatformPlaylistExportRequest, DJSEMPlatformPlaylistExportResult, DJSEMPlatformPlaylistImportReport, DJSEMPlatformPlaylistRef, DJSEMPlatformProvider, DJSEMPlatformTrackMatchCandidate, DJSEMPlatformTrackMatchDecision, DJSEMPlatformTrackRef, DJSEMSoundCloudPlaylistImportRequest, DJSEMSoundCloudTrackRef, DJSEMSpotifyPlaylistImportRequest, DJSEMSpotifyTrackRef, DJSEMTrackMatchConfidence, DJSEMTrackMatchReason, FindPlatformTrackMatchesInput, parseSoundCloudPlaylistUrl, parseSpotifyPlaylistId } from "./types";
const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();


/** Matches platform track references against the local library, and builds DJSEM sets from confirmed matches. */
export class PlatformTrackMatchingService {
  /**
   * Find match candidates for each platform track against the DJSEM library.
   * Conservative scoring: ISRC > title+artist exact > title+artist+duration fuzzy.
   * Low-confidence matches require user confirmation.
   * @param input - the platform track refs, candidate library tracks, and any previously confirmed decisions.
   * @returns all candidate matches found (possibly several per platform track ref), each with a confidence and score.
   */
  findMatchCandidates(input: FindPlatformTrackMatchesInput): DJSEMPlatformTrackMatchCandidate[] {
    const tol = input.durationToleranceMs ?? 3000;
    const prevByRef = new Map<string, DJSEMPlatformTrackMatchDecision>();
    for (const d of input.previousDecisions ?? []) {
      if (d.decision === "matched" && d.selectedDjsemTrackId) prevByRef.set(d.platformTrackRefId, d);
    }

    const candidates: DJSEMPlatformTrackMatchCandidate[] = [];

    for (const ref of input.platformTrackRefs) {
      const refId = ref.id;
      const refTitle = normalize(ref.title);
      const refArtist = normalize(ref.provider === "spotify" ? (ref as DJSEMSpotifyTrackRef).artists.join(", ") : ((ref as DJSEMSoundCloudTrackRef).artist ?? (ref as DJSEMSoundCloudTrackRef).userDisplayName));
      const refDur = ref.durationMs;
      const refIsrc = ref.provider === "spotify" ? (ref as DJSEMSpotifyTrackRef).isrc : undefined;

      // Previously confirmed match — reuse directly.
      const prev = prevByRef.get(refId);
      if (prev && prev.selectedDjsemTrackId) {
        candidates.push({ platformTrackRefId: refId, djsemTrackId: prev.selectedDjsemTrackId, confidence: "exact", score: 1.0, reasons: ["previously_confirmed"], warnings: [] });
        continue;
      }

      for (const lib of input.libraryTracks) {
        let score = 0;
        const reasons: DJSEMTrackMatchReason[] = [];

        // ISRC exact match (strongest signal).
        if (refIsrc && lib.isrc && refIsrc.toUpperCase() === lib.isrc.toUpperCase()) {
          score += 0.6;
          reasons.push("isrc_match");
        }

        // Title + artist exact normalized match.
        const libTitle = normalize(lib.title);
        const libArtist = normalize(lib.artist);
        if (refTitle && libTitle && refTitle === libTitle) {
          if (refArtist && libArtist && refArtist === libArtist) {
            score += 0.35;
            reasons.push("title_artist_exact");
          } else {
            // Title matches but artist differs — low signal, needs user confirmation.
            score += 0.1;
            reasons.push("title_artist_exact"); // used loosely: title matched, artist did not
          }
        }

        // Duration within tolerance.
        if (refDur && lib.durationMs && Math.abs(refDur - lib.durationMs) <= tol) {
          score += 0.05;
          reasons.push("duration_close");
        }

        if (score > 0 && reasons.length > 0) {
          let confidence: DJSEMTrackMatchConfidence = "none";
          if (score >= 0.75) confidence = "exact";
          else if (score >= 0.5) confidence = "high";
          else if (score >= 0.3) confidence = "medium";
          else if (score > 0) confidence = "low";
          candidates.push({ platformTrackRefId: refId, djsemTrackId: lib.preparationTrackId, confidence, score, reasons, warnings: confidence === "low" ? ["Low-confidence match — user confirmation required."] : [] });
        }
      }
    }
    return candidates;
  }

  /**
   * Reduces a candidate list to the single highest-scoring candidate per platform track ref.
   * @param candidates - the candidate matches to reduce.
   * @returns a map of platform track ref id to its best candidate.
   */
  bestCandidatePerRef(candidates: DJSEMPlatformTrackMatchCandidate[]): Map<string, DJSEMPlatformTrackMatchCandidate> {
    const best = new Map<string, DJSEMPlatformTrackMatchCandidate>();
    for (const c of candidates) {
      const existing = best.get(c.platformTrackRefId);
      if (!existing || c.score > existing.score) best.set(c.platformTrackRefId, c);
    }
    return best;
  }

  /**
   * Creates a DJSEM Set from confirmed match decisions, preserving playlist order.
   * @param input - the platform track refs (in playlist order) plus their match decisions.
   * @returns a partial set (with matched tracks only) and warnings for any skipped unmatched tracks.
   */
  createDJSEMSetFromMatches(input: CreateDJSEMSetFromPlatformInput): { set: Partial<DJSEMSet>; warnings: string[] } {
    const warnings: string[] = [];
    const tracks: Array<{ id: string; preparationTrackId: string; position: number; locked: boolean; warnings: string[] }> = [];
    let pos = 0;
    for (const ref of input.platformTrackRefs) {
      const decision = input.matchDecisions.find((d) => d.platformTrackRefId === ref.id);
      if (!decision || decision.decision !== "matched" || !decision.selectedDjsemTrackId) {
        warnings.push(`Unmatched platform track '${ref.title}' skipped.`);
        continue;
      }
      tracks.push({
        id: id("st"), preparationTrackId: decision.selectedDjsemTrackId,
        position: pos++, locked: false, warnings: [],
      });
    }
    return {
      set: { id: id("set"), title: input.title, tracks, createdAt: AT, updatedAt: AT, reviewStatus: "draft" as const } as Partial<DJSEMSet>,
      warnings,
    };
  }
}

// ============================================================================
// SECTION 5 — Spotify Playlist Import Adapter (04E)
// ============================================================================

/** Resolves, normalizes, and reports on Spotify playlist imports (metadata only, no audio). */
export class SpotifyPlaylistImportAdapter {
  /**
   * Validates that a Spotify import request has the fields required for its source type.
   * @param req - the import request to validate.
   * @returns the validation result.
   */
  validateRequest(req: DJSEMSpotifyPlaylistImportRequest): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (req.source === "playlist_url" && !req.spotifyPlaylistUrl) issues.push({ path: "$.spotifyPlaylistUrl", message: "required when source is playlist_url" });
    if (req.source === "playlist_id" && !req.spotifyPlaylistId) issues.push({ path: "$.spotifyPlaylistId", message: "required when source is playlist_id" });
    if (req.source === "current_user_playlist" && !req.connectedAccountId) issues.push({ path: "$.connectedAccountId", message: "required for current_user_playlist" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "spotify_import_request");
  }

  /**
   * Parses a Spotify URL/URI into a resolved playlist ref (metadata only — no audio).
   * @param input - a playlist id and/or URL, plus optional fixture payload (real API calls happen in the worker layer).
   * @returns the resolved playlist ref, or `undefined` if no playlist id could be determined.
   */
  resolvePlaylist(input: { spotifyPlaylistId?: string; spotifyPlaylistUrl?: string; fixtureData?: unknown }): DJSEMPlatformPlaylistRef | undefined {
    let playlistId = input.spotifyPlaylistId;
    if (!playlistId && input.spotifyPlaylistUrl) playlistId = parseSpotifyPlaylistId(input.spotifyPlaylistUrl);
    if (!playlistId) return undefined;
    // Fixture pathway — in a real implementation the worker calls Spotify's API here.
    if (input.fixtureData && typeof input.fixtureData === "object") {
      const fx = input.fixtureData as Record<string, unknown>;
      return {
        id: id("ppr"), provider: "spotify", providerPlaylistId: playlistId,
        url: `https://open.spotify.com/playlist/${playlistId}`,
        title: (fx["name"] as string) ?? "Untitled",
        description: (fx["description"] as string) ?? undefined,
        ownerDisplayName: ((fx["owner"] as Record<string, unknown> | undefined)?.["display_name"] as string) ?? undefined,
        public: (fx["public"] as boolean) ?? undefined,
        collaborative: (fx["collaborative"] as boolean) ?? undefined,
        itemCount: (fx["tracks"] as Record<string, unknown> | undefined)?.["total"] as number ?? undefined,
        importedAt: AT, warnings: [],
      };
    }
    // Stub when no fixture and no API: return ref with warning.
    return {
      id: id("ppr"), provider: "spotify", providerPlaylistId: playlistId,
      url: `https://open.spotify.com/playlist/${playlistId}`,
      title: "Spotify playlist", importedAt: AT,
      warnings: ["Resolved from URL only — full metadata requires Spotify API call."],
    };
  }

  /**
   * Normalizes a raw Spotify API track payload into a {@link DJSEMSpotifyTrackRef}.
   * @param raw - the raw Spotify track (or playlist-item wrapping a track) payload.
   * @returns the normalized track reference, with warnings for local/restricted tracks.
   */
  normalizeTrack(raw: Record<string, unknown>): DJSEMSpotifyTrackRef {
    const track = (raw["track"] ?? raw) as Record<string, unknown>;
    const id_val = (track["id"] as string) ?? `unknown_${_seq.next()}`;
    const artists = ((track["artists"] as Array<Record<string, unknown>> | undefined) ?? []).map((a) => (a["name"] as string) ?? "");
    const album = track["album"] as Record<string, unknown> | undefined;
    const extIds = (track["external_ids"] as Record<string, unknown> | undefined) ?? {};
    const warnings: string[] = [];
    if (track["is_local"]) warnings.push("Local Spotify track — cannot match by ISRC.");
    if ((track["restrictions"] as Record<string, unknown> | undefined)?.["reason"]) warnings.push("Track has playback restrictions.");
    return {
      id: id("str"), provider: "spotify",
      spotifyTrackId: id_val,
      spotifyUri: (track["uri"] as string) ?? undefined,
      url: ((track["external_urls"] as Record<string, unknown> | undefined)?.["spotify"] as string) ?? undefined,
      title: (track["name"] as string) ?? "Unknown",
      artists,
      albumTitle: (album?.["name"] as string) ?? undefined,
      durationMs: (track["duration_ms"] as number) ?? undefined,
      explicit: (track["explicit"] as boolean) ?? undefined,
      isrc: (extIds["isrc"] as string) ?? undefined,
      popularity: (track["popularity"] as number) ?? undefined,
      warnings,
    };
  }

  /**
   * Builds a summary import report from a set of track refs and their match decisions.
   * @param input - the provider, source playlist identity, track refs, match decisions, and created set id (if any).
   * @returns the import report with matched/unmatched counts.
   */
  createImportReport(input: {
    provider: DJSEMPlatformProvider; sourcePlaylistId: string; sourcePlaylistTitle?: string;
    trackRefs: DJSEMPlatformTrackRef[]; matchDecisions: DJSEMPlatformTrackMatchDecision[];
    createdSetId?: string;
  }): DJSEMPlatformPlaylistImportReport {
    const matched = input.matchDecisions.filter((d) => d.decision === "matched").length;
    return {
      id: id("pir"), provider: input.provider,
      sourcePlaylistId: input.sourcePlaylistId, sourcePlaylistTitle: input.sourcePlaylistTitle,
      totalItems: input.trackRefs.length, importedItems: input.trackRefs.length,
      skippedItems: 0, matchedItems: matched, unmatchedItems: input.trackRefs.length - matched,
      createdSetId: input.createdSetId,
      warnings: [], errors: [], createdAt: AT,
    };
  }
}

// ============================================================================
// SECTION 6 — SoundCloud Playlist Import Adapter (04F)
// ============================================================================

/** Resolves, normalizes, and reports on SoundCloud playlist imports (metadata only, no stream capture). */
export class SoundCloudPlaylistImportAdapter {
  /**
   * Validates that a SoundCloud import request has the fields required for its source type.
   * @param req - the import request to validate.
   * @returns the validation result.
   */
  validateRequest(req: DJSEMSoundCloudPlaylistImportRequest): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (req.source === "playlist_url" && !req.soundcloudPlaylistUrl) issues.push({ path: "$.soundcloudPlaylistUrl", message: "required when source is playlist_url" });
    if (req.source === "playlist_id" && !req.soundcloudPlaylistId) issues.push({ path: "$.soundcloudPlaylistId", message: "required when source is playlist_id" });
    if (req.source === "current_user_playlist" && !req.connectedAccountId) issues.push({ path: "$.connectedAccountId", message: "required for current_user_playlist" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "soundcloud_import_request");
  }

  /**
   * Resolves a SoundCloud URL/id into a resolved playlist ref (metadata only).
   * @param input - a playlist id and/or URL, plus optional fixture payload (real API calls happen in the worker layer).
   * @returns the resolved playlist ref, or `undefined` if no playlist id could be determined.
   */
  resolvePlaylist(input: { soundcloudPlaylistUrl?: string; soundcloudPlaylistId?: string; fixtureData?: unknown }): DJSEMPlatformPlaylistRef | undefined {
    let playlistId = input.soundcloudPlaylistId;
    if (!playlistId && input.soundcloudPlaylistUrl) {
      const parsed = parseSoundCloudPlaylistUrl(input.soundcloudPlaylistUrl);
      if (parsed) playlistId = `${parsed.user}/${parsed.slug}`;
    }
    if (!playlistId) return undefined;
    if (input.fixtureData && typeof input.fixtureData === "object") {
      const fx = input.fixtureData as Record<string, unknown>;
      return {
        id: id("scpr"), provider: "soundcloud", providerPlaylistId: playlistId,
        url: `https://soundcloud.com/${playlistId.replace(/\//, "/sets/")}`,
        title: (fx["title"] as string) ?? "Untitled",
        description: (fx["description"] as string) ?? undefined,
        ownerDisplayName: ((fx["user"] as Record<string, unknown> | undefined)?.["username"] as string) ?? undefined,
        itemCount: (fx["track_count"] as number) ?? undefined,
        importedAt: AT, warnings: [],
      };
    }
    return {
      id: id("scpr"), provider: "soundcloud", providerPlaylistId: playlistId,
      title: "SoundCloud playlist", importedAt: AT,
      warnings: ["Resolved from URL only — full metadata requires SoundCloud API call."],
    };
  }

  /**
   * Normalizes a raw SoundCloud API track payload into a {@link DJSEMSoundCloudTrackRef}.
   * @param raw - the raw SoundCloud track payload.
   * @returns the normalized track reference, with a warning if the artist/user field is missing.
   */
  normalizeTrack(raw: Record<string, unknown>): DJSEMSoundCloudTrackRef {
    const warnings: string[] = [];
    if (!raw["user"]) warnings.push("Missing user/artist field.");
    const user = raw["user"] as Record<string, unknown> | undefined;
    return {
      id: id("sctr"), provider: "soundcloud",
      soundcloudTrackId: String(raw["id"] ?? `unknown_${_seq.next()}`),
      url: (raw["permalink_url"] as string) ?? undefined,
      title: (raw["title"] as string) ?? "Unknown",
      artist: (user?.["username"] as string) ?? undefined,
      userDisplayName: (user?.["username"] as string) ?? undefined,
      durationMs: (raw["duration"] as number) ?? undefined,
      genre: (raw["genre"] as string) ?? undefined,
      bpm: (raw["bpm"] as number) ?? undefined,
      tagList: typeof raw["tag_list"] === "string" ? (raw["tag_list"] as string).split(" ").filter(Boolean) : undefined,
      downloadable: (raw["downloadable"] as boolean) ?? undefined,
      streamable: (raw["streamable"] as boolean) ?? undefined,
      license: (raw["license"] as string) ?? undefined,
      warnings,
    };
  }

  /**
   * Reports SoundCloud API capabilities for the current connection — many write endpoints are
   * restricted/unavailable for most apps, so this conservatively defaults to unsupported.
   * @param input - whether the SoundCloud API is configured and whether write access is verified.
   * @returns the capabilities, defaulting to no remote writes unless write access is confirmed.
   */
  getCapabilities(input: { apiConfigured: boolean; hasWriteAccess: boolean }): DJSEMPlatformPlaylistCapabilities {
    if (!input.apiConfigured) {
      return {
        provider: "soundcloud",
        canCreatePlaylist: false, canUpdatePlaylist: false, canAppendItems: false,
        canReplaceItems: false, canReorderItems: false,
        requiredScopes: [], warnings: ["SoundCloud API not configured."],
        unsupportedReasons: ["SoundCloud API credentials not available. Falling back to manual/fixture import."],
      };
    }
    return {
      provider: "soundcloud",
      canCreatePlaylist: input.hasWriteAccess, canUpdatePlaylist: false,
      canAppendItems: false, canReplaceItems: false, canReorderItems: false,
      requiredScopes: input.hasWriteAccess ? ["non-expiring"] : [],
      warnings: input.hasWriteAccess ? [] : ["SoundCloud write access not verified — export defaults to dry-run only."],
      unsupportedReasons: input.hasWriteAccess ? [] : ["SoundCloud playlist update is not officially supported for most apps."],
    };
  }

  /**
   * Builds a summary import report from a set of SoundCloud track refs and their match decisions.
   * @param input - the source playlist identity, track refs, match decisions, and created set id (if any).
   * @returns the import report with matched/unmatched counts.
   */
  createImportReport(input: {
    sourcePlaylistId: string; sourcePlaylistTitle?: string;
    trackRefs: DJSEMSoundCloudTrackRef[]; matchDecisions: DJSEMPlatformTrackMatchDecision[];
    createdSetId?: string;
  }): DJSEMPlatformPlaylistImportReport {
    const matched = input.matchDecisions.filter((d) => d.decision === "matched").length;
    return {
      id: id("pir"), provider: "soundcloud",
      sourcePlaylistId: input.sourcePlaylistId, sourcePlaylistTitle: input.sourcePlaylistTitle,
      totalItems: input.trackRefs.length, importedItems: input.trackRefs.length,
      skippedItems: 0, matchedItems: matched, unmatchedItems: input.trackRefs.length - matched,
      createdSetId: input.createdSetId,
      warnings: [], errors: [], createdAt: AT,
    };
  }
}

// ============================================================================
// SECTION 7 — Platform Playlist Export Service (04G)
// ============================================================================

// Spotify official: POST /v1/users/{user_id}/playlists + POST /v1/playlists/{id}/tracks
// Max 100 items per add-items call. Required OAuth scopes documented in Spotify Web API docs.
const SPOTIFY_MAX_ITEMS_PER_ADD = 100;

/** Plans, executes, and audits playlist exports to remote platforms, always requiring explicit user confirmation. */
export class PlatformPlaylistExportService {
  /**
   * Validates an export request, requiring explicit user confirmation for any non-dry-run mode.
   * @param req - the export request to validate.
   * @returns the validation result.
   */
  validateRequest(req: DJSEMPlatformPlaylistExportRequest): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!req.connectedAccountId) issues.push({ path: "$.connectedAccountId", message: "required" });
    if (!req.sourceId) issues.push({ path: "$.sourceId", message: "required" });
    if (!req.remotePlaylistName.trim()) issues.push({ path: "$.remotePlaylistName", message: "required" });
    if (!req.userConfirmedRemoteWrite && req.exportMode !== "dry_run_only") {
      issues.push({ path: "$.userConfirmedRemoteWrite", message: "user must confirm remote write" });
    }
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "platform_export_request");
  }

  /**
   * Build an export plan — a dry-run that shows what WOULD be written.
   * Every DJSEM track in the set must have a confirmed platform track mapping or be
   * flagged as unmatched. The plan is a preview; it never touches the remote.
   * @param input - the export request, the set's tracks, their match decisions, and the provider's capabilities.
   * @returns the export plan with per-item status, ready/unmatched counts, and whether it `canExecute`.
   */
  createExportPlan(input: {
    request: DJSEMPlatformPlaylistExportRequest;
    setTracks: Array<{ djsemTrackId: string }>;
    matchDecisions: DJSEMPlatformTrackMatchDecision[];
    capabilities: DJSEMPlatformPlaylistCapabilities;
  }): DJSEMPlatformPlaylistExportPlan {
    const decisionByRef = new Map<string, DJSEMPlatformTrackMatchDecision>();
    for (const d of input.matchDecisions) {
      if (d.selectedDjsemTrackId) decisionByRef.set(d.selectedDjsemTrackId, d);
    }

    const items: DJSEMPlatformPlaylistExportItem[] = [];
    let ready = 0, unmatched = 0;
    const blocked = 0;
    const seen = new Set<string>();
    for (let i = 0; i < input.setTracks.length; i++) {
      const t = input.setTracks[i];
      const d = decisionByRef.get(t.djsemTrackId);
      if (!d || d.decision !== "matched" || !d.selectedDjsemTrackId) {
        unmatched++;
        items.push({ index: i, djsemTrackId: t.djsemTrackId, provider: input.request.provider, status: "unmatched", warnings: ["No platform track mapping."] });
        continue;
      }
      // Provider track ID is the selectedDjsemTrackId → but we need the *platform* track ID.
      // In practice the matchDecision links platformTrackRefId → djsemTrackId. To look up the
      // provider track ID we reverse the map: djsemTrackId → platformTrackRefId.
      const providerTrackId = d.platformTrackRefId;
      if (seen.has(providerTrackId)) {
        items.push({ index: i, djsemTrackId: t.djsemTrackId, provider: input.request.provider, providerTrackId, status: "duplicate", warnings: ["Duplicate provider track."] });
        continue;
      }
      seen.add(providerTrackId);
      ready++;
      items.push({ index: i, djsemTrackId: t.djsemTrackId, provider: input.request.provider, providerTrackId, status: "ready", warnings: [] });
    }

    const warnings: string[] = [];
    const errors: string[] = [];
    if (unmatched > 0 && !input.request.userConfirmedUnmatchedTracks && !input.request.includeOnlyMatchedTracks) {
      errors.push(`${unmatched} unmatched track(s) — user must confirm or enable includeOnlyMatchedTracks.`);
    }
    if (input.request.exportMode !== "dry_run_only" && input.request.exportMode !== "create_new_remote_playlist") {
      if (!input.capabilities.canUpdatePlaylist) {
        errors.push(`Provider '${input.request.provider}' does not support remote playlist updates.`);
      }
    }
    const canExecute = errors.length === 0 && ready > 0 && input.request.exportMode !== "dry_run_only";
    return {
      id: id("pep"), requestId: input.request.id, provider: input.request.provider,
      exportMode: input.request.exportMode, sourceId: input.request.sourceId,
      targetRemotePlaylistId: input.request.remotePlaylistId,
      targetRemotePlaylistName: input.request.remotePlaylistName,
      items, readyCount: ready, unmatchedCount: unmatched, blockedCount: blocked,
      warnings, errors, canExecute, createdAt: AT,
    };
  }

  /**
   * Chunks an export plan's ready items into batches for Spotify's 100-item-per-call add-items limit.
   * @param items - the export plan's items (only `"ready"` items are included in the chunks).
   * @returns the ready items split into chunks of at most 100.
   */
  chunkForSpotify(items: DJSEMPlatformPlaylistExportItem[]): DJSEMPlatformPlaylistExportItem[][] {
    const ready = items.filter((i) => i.status === "ready");
    const chunks: DJSEMPlatformPlaylistExportItem[][] = [];
    for (let i = 0; i < ready.length; i += SPOTIFY_MAX_ITEMS_PER_ADD) {
      chunks.push(ready.slice(i, i + SPOTIFY_MAX_ITEMS_PER_ADD));
    }
    return chunks;
  }

  /**
   * Executes an export against the remote platform (stub — the real remote call is made by the
   * worker's job runner, optionally via the injected `writeAdapter`).
   * @param input.plan - the (already-validated) export plan to execute.
   * @param input.writeAdapter - injected write adapter; the worker provides the real Spotify/SC API client.
   * @param input.completedAt - override for the completion timestamp (defaults to the fixed epoch).
   * @returns the export result, `"unsupported"`/`"failed"` immediately if `plan.canExecute` is false.
   */
  executeExport(input: {
    plan: DJSEMPlatformPlaylistExportPlan;
    /** Injected write adapter — the worker provides the real Spotify/SC API client. */
    writeAdapter?: { createPlaylist: (name: string, items: string[]) => Promise<{ playlistId: string; url: string; snapshotId?: string }> };
    completedAt?: string;
  }): DJSEMPlatformPlaylistExportResult {
    if (!input.plan.canExecute) {
      return {
        id: id("per"), planId: input.plan.id, provider: input.plan.provider,
        status: input.plan.errors.length > 0 ? "failed" : "unsupported",
        itemsWritten: 0, itemsSkipped: input.plan.items.length,
        warnings: input.plan.warnings, errors: input.plan.errors,
        completedAt: input.completedAt ?? AT,
      };
    }
    // Stub result — real remote call happens in the worker's job runner.
    const readyItems = input.plan.items.filter((i) => i.status === "ready");
    return {
      id: id("per"), planId: input.plan.id, provider: input.plan.provider,
      status: "completed",
      remotePlaylistId: `remote_${input.plan.id}`,
      remotePlaylistUrl: input.plan.provider === "spotify" ? `https://open.spotify.com/playlist/remote_${input.plan.id}` : undefined,
      itemsWritten: readyItems.length, itemsSkipped: input.plan.items.length - readyItems.length,
      warnings: input.plan.warnings, errors: [],
      completedAt: input.completedAt ?? AT,
    };
  }

  /**
   * Builds an audit trail entry summarising a completed export (or import) operation.
   * @param input - the export result and plan, plus the operation kind being audited.
   * @returns the audit report, combining warnings/errors from both the plan and the result.
   */
  createAuditReport(input: {
    result: DJSEMPlatformPlaylistExportResult;
    plan: DJSEMPlatformPlaylistExportPlan;
    operation: "import" | "export";
  }): DJSEMPlatformPlaylistAuditReport {
    return {
      id: id("par"), provider: input.plan.provider, operation: input.operation,
      sourceId: input.plan.sourceId, totalItems: input.plan.items.length,
      matchedItems: input.plan.readyCount, unmatchedItems: input.plan.unmatchedCount,
      writtenItems: input.result.itemsWritten, skippedItems: input.result.itemsSkipped,
      warnings: [...input.plan.warnings, ...input.result.warnings],
      errors: [...input.plan.errors, ...input.result.errors],
      createdAt: AT,
    };
  }
}

// ============================================================================
// SECTION 8 — QA Harness helpers (04H)
// ============================================================================

/** QA harness helpers that scan source text for guardrail-violating code patterns (audio download, unsafe remote mutation). */
export class PlatformPlaylistQAHarness {
  /**
   * Structural code audit: ensure no audio-download/rip/stream-capture patterns exist.
   * Returns issues found. Real QA runs this against the source file text.
   * @param sourceText - the source file text to scan.
   * @returns the validation result, invalid if any dangerous pattern is found.
   */
  assertNoAudioDownloadCode(sourceText: string): DJSEMValidationResult {
    const dangerous = [
      /download.*audio/i, /rip.*stream/i, /stream_url/i, /download_url/i,
      /fetch\s+audio/i, /youtube.dl/i, /yt.dlp/i, /ffmpeg\s+spotify/i, /ffmpeg\s+soundcloud/i,
    ];
    const issues: ValidationResult["issues"] = [];
    for (const rx of dangerous) {
      if (rx.test(sourceText)) issues.push({ path: "$", message: `Dangerous pattern found: ${rx.source}` });
    }
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "no_audio_download");
  }

  /**
   * Structural code audit: ensure no unsafe remote mutation or client-side token storage patterns exist.
   * @param sourceText - the source file text to scan.
   * @returns the validation result, invalid if any risky pattern is found.
   */
  assertNoUnsafeRemoteMutation(sourceText: string): DJSEMValidationResult {
    const risky = [
      /localStorage.*refresh_token/i, /localStorage.*accessToken/i,
      /delete.*playlist.*item/i, /replace.*playlist/i, /remove.*tracks/i,
    ];
    const issues: ValidationResult["issues"] = [];
    for (const rx of risky) {
      if (rx.test(sourceText)) issues.push({ path: "$", message: `Risky mutation pattern: ${rx.source}` });
    }
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "no_unsafe_mutation");
  }
}
